import { chat, executeTool, determineStepType } from '@xsai/shared-chat';

const strCamelToSnake = (str) => str.replace(/[A-Z]/g, (s) => `_${s.toLowerCase()}`);
const objCamelToSnake = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [strCamelToSnake(k), v]));

const trampoline = async (fn) => {
  let result = await fn();
  while (result instanceof Function)
    result = await result();
  return result;
};

class DelayedPromise {
  get promise() {
    if (this._promise == null) {
      this._promise = new Promise((resolve, reject) => {
        if (this.status.type === "resolved") {
          resolve(this.status.value);
        } else if (this.status.type === "rejected") {
          reject(this.status.error);
        }
        this._resolve = resolve;
        this._reject = reject;
      });
    }
    return this._promise;
  }
  _promise;
  _reject;
  _resolve;
  status = { type: "pending" };
  reject(error) {
    this.status = { error, type: "rejected" };
    if (this._promise) {
      this._reject?.(error);
    }
  }
  resolve(value) {
    this.status = { type: "resolved", value };
    if (this._promise) {
      this._resolve?.(value);
    }
  }
}

const parseChunk = (text) => {
  if (!text || !text.startsWith("data:"))
    return [void 0, false];
  const content = text.slice("data:".length);
  const data = content.startsWith(" ") ? content.slice(1) : content;
  if (data === "[DONE]") {
    return [void 0, true];
  }
  if (data.startsWith("{") && data.includes('"error":')) {
    throw new Error(`Error from server: ${data}`);
  }
  const chunk = JSON.parse(data);
  return [chunk, false];
};
const transformChunk = () => {
  const decoder = new TextDecoder();
  let buffer = "";
  return new TransformStream({
    transform: async (chunk, controller) => {
      const text = decoder.decode(chunk, { stream: true });
      buffer += text;
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        try {
          const [chunk2, isEnd] = parseChunk(line);
          if (isEnd)
            break;
          if (chunk2) {
            controller.enqueue(chunk2);
          }
        } catch (error) {
          controller.error(error);
        }
      }
    }
  });
};

const streamText = async (options) => {
  const steps = [];
  const messages = structuredClone(options.messages);
  const maxSteps = options.maxSteps ?? 1;
  let usage;
  const resultSteps = new DelayedPromise();
  const resultMessages = new DelayedPromise();
  const resultUsage = new DelayedPromise();
  let eventCtrl;
  let textCtrl;
  const eventStream = new ReadableStream({ start: (controller) => eventCtrl = controller });
  const textStream = new ReadableStream({ start: (controller) => textCtrl = controller });
  const pushEvent = (stepEvent) => {
    eventCtrl?.enqueue(stepEvent);
    void options.onEvent?.(stepEvent);
  };
  const pushStep = (step) => {
    steps.push(step);
    void options.onStepFinish?.(step);
  };
  const startStream = async () => {
    const pushUsage = (u) => {
      usage = u;
    };
    let text = "";
    const pushText = (content) => {
      textCtrl?.enqueue(content);
      text += content;
    };
    const tool_calls = [];
    const toolCalls = [];
    const toolResults = [];
    let finishReason = "other";
    await chat({
      ...options,
      maxSteps: void 0,
      messages,
      stream: true,
      streamOptions: options.streamOptions != null ? objCamelToSnake(options.streamOptions) : void 0
    }).then(
      async (res) => res.body.pipeThrough(transformChunk()).pipeTo(new WritableStream({
        abort: (reason) => {
          eventCtrl?.error(reason);
          textCtrl?.error(reason);
        },
        close: () => {
        },
        write: (chunk) => {
          if (chunk.usage)
            pushUsage(chunk.usage);
          if (chunk.choices == null || chunk.choices.length === 0)
            return;
          const choice = chunk.choices[0];
          if (choice.finish_reason != null)
            finishReason = choice.finish_reason;
          if (choice.delta.tool_calls?.length === 0 || choice.delta.tool_calls == null) {
            if (choice.delta.content != null) {
              pushEvent({ text: choice.delta.content, type: "text-delta" });
              pushText(choice.delta.content);
            } else if (choice.delta.refusal != null) {
              pushEvent({ error: choice.delta.refusal, type: "error" });
            } else if (choice.finish_reason != null) {
              pushEvent({ finishReason: choice.finish_reason, type: "finish", usage });
            }
          } else {
            for (const toolCall of choice.delta.tool_calls) {
              const { index } = toolCall;
              if (!tool_calls.at(index)) {
                tool_calls[index] = toolCall;
                pushEvent({ toolCallId: toolCall.id, toolName: toolCall.function.name, type: "tool-call-streaming-start" });
              } else {
                tool_calls[index].function.arguments += toolCall.function.arguments;
                pushEvent({ argsTextDelta: toolCall.function.arguments, toolCallId: toolCall.id, toolName: toolCall.function.name, type: "tool-call-delta" });
              }
            }
          }
        }
      }))
    );
    messages.push({ content: text, role: "assistant", tool_calls });
    if (tool_calls.length !== 0) {
      for (const toolCall of tool_calls) {
        const { completionToolCall, completionToolResult, message } = await executeTool({
          abortSignal: options.abortSignal,
          messages,
          toolCall,
          tools: options.tools
        });
        toolCalls.push(completionToolCall);
        toolResults.push(completionToolResult);
        messages.push(message);
        pushEvent({ ...completionToolCall, type: "tool-call" });
        pushEvent({ ...completionToolResult, type: "tool-result" });
      }
    } else {
      pushEvent({
        finishReason,
        type: "finish",
        usage
      });
    }
    pushStep({
      finishReason,
      stepType: determineStepType({ finishReason, maxSteps, stepsLength: steps.length, toolCallsLength: toolCalls.length }),
      text,
      toolCalls,
      toolResults,
      usage
    });
    if (toolCalls.length !== 0 && steps.length < maxSteps)
      return async () => startStream();
  };
  try {
    await trampoline(async () => startStream());
    eventCtrl?.close();
    textCtrl?.close();
  } catch (err) {
    eventCtrl?.error(err);
    textCtrl?.error(err);
    resultSteps.reject(err);
    resultMessages.reject(err);
    resultUsage.reject(err);
  } finally {
    resultSteps.resolve(steps);
    resultMessages.resolve(messages);
    resultUsage.resolve(usage);
    void options.onFinish?.(steps.at(-1));
  }
  return {
    fullStream: eventStream,
    messages: resultMessages.promise,
    steps: resultSteps.promise,
    textStream,
    usage: resultUsage.promise
  };
};

export { streamText };
